'use client';

import { ColumnDef } from '@tanstack/react-table';
import {
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  ExternalLink,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Firm } from '@/services/types/firms-types';
import { getUserDisplayName } from '@/utils/user';

import { FirmStatusBadge } from '../ui/FirmStatusBadge';

const FALLBACK_TEXT = 'Not set';

// Column header component
interface DataTableColumnHeaderProps {
  column: any;
  title: string;
  className?: string;
}

export const DataTableColumnHeader = ({
  column,
  title,
  className,
}: DataTableColumnHeaderProps) => {
  if (!column.getCanSort()) {
    return <div className={className}>{title}</div>;
  }

  // Replace nested ternary with variable assignment
  let SortIcon = ChevronsUpDown;
  let iconClassName = 'ml-1.5 h-3.5 w-3.5 text-gray-400';

  if (column.getIsSorted() === 'desc') {
    SortIcon = ChevronDown;
    iconClassName = 'ml-1.5 h-3.5 w-3.5 text-gray-700';
  } else if (column.getIsSorted() === 'asc') {
    SortIcon = ChevronUp;
    iconClassName = 'ml-1.5 h-3.5 w-3.5 text-gray-700';
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 data-[state=open]:bg-accent p-0 font-semibold text-sm text-gray-900"
      onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
    >
      <span>{title}</span>
      <SortIcon className={iconClassName} />
    </Button>
  );
};

export const columns: ColumnDef<Firm>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Firm Name" />
    ),
    cell: ({ row }) => {
      const firm = row.original;
      return (
        <div className="flex flex-col max-w-[140px]">
          <div className="font-medium text-gray-900 truncate text-xs">
            {firm.name}
          </div>
          {firm.full_name && (
            <div className="text-gray-500 text-[10px] truncate">
              {firm.full_name}
            </div>
          )}
        </div>
      );
    },
    enableSorting: true,
  },
  {
    accessorKey: 'email',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Email" />
    ),
    cell: ({ row }) => {
      return (
        <div className="text-gray-500 truncate max-w-[140px] text-[11px]">
          {row.original.email || '—'}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: 'type',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Type" />
    ),
    cell: ({ row }) => {
      return (
        <div className="text-gray-600 truncate text-[11px]">
          {row.original.type || '—'}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: 'primary_state',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="State" />
    ),
    cell: ({ row }) => {
      return (
        <div className="text-gray-600 truncate text-[11px]">
          {row.original.primary_state || '—'}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: 'managing_paralegal',
    accessorFn: (firm) =>
      firm.managing_paralegal
        ? getUserDisplayName(firm.managing_paralegal)
        : FALLBACK_TEXT,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Paralegal" />
    ),
    cell: ({ row }) => {
      const displayValue = row.original.managing_paralegal
        ? getUserDisplayName(row.original.managing_paralegal)
        : FALLBACK_TEXT;

      return (
        <div className="text-gray-600 truncate text-[11px] max-w-[100px]">
          {displayValue ?? FALLBACK_TEXT}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: 'status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      return row.original.status ? (
        <div>
          <FirmStatusBadge status={row.original.status} />
        </div>
      ) : null;
    },
    enableSorting: true,
  },
  {
    accessorKey: 'cms_system',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="CMS" />
    ),
    cell: ({ row }) => {
      const system = row.original.cms_system;
      const link = row.original.cms_link;

      if (!system) return <div className="text-gray-400 text-[11px]">—</div>;

      if (link) {
        return (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 flex items-center gap-0.5 truncate max-w-[100px] text-[11px]"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="truncate">{system}</span>
            <ExternalLink className="h-2.5 w-2.5 flex-shrink-0" />
          </a>
        );
      }

      return (
        <div className="text-gray-600 truncate max-w-[100px] text-[11px]">
          {system}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: 'lead_system',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Lead" />
    ),
    cell: ({ row }) => {
      const system = row.original.lead_system;
      const link = row.original.lead_system_link;

      if (!system) return <div className="text-gray-400 text-[11px]">—</div>;

      if (link) {
        return (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 flex items-center gap-0.5 truncate max-w-[100px] text-[11px]"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="truncate">{system}</span>
            <ExternalLink className="h-2.5 w-2.5 flex-shrink-0" />
          </a>
        );
      }

      return (
        <div className="text-gray-600 truncate max-w-[100px] text-[11px]">
          {system}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: 'commentary',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Commentary" />
    ),
    cell: ({ row }) => {
      const text = row.original.commentary;
      if (!text) return <div className="text-gray-400 text-[11px]">—</div>;
      return (
        <div className="text-gray-600 max-w-[180px] text-[11px] whitespace-pre-wrap break-words">
          {text}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: 'errors',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Errors" />
    ),
    cell: ({ row }) => {
      const text = row.original.errors;
      if (!text) return <div className="text-gray-400 text-[11px]">—</div>;
      return (
        <div className="text-gray-600 max-w-[180px] text-[11px] whitespace-pre-wrap break-words">
          {text}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: 'plan_to_derisk',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Derisk Plan" />
    ),
    cell: ({ row }) => {
      const text = row.original.plan_to_derisk;
      if (!text) return <div className="text-gray-400 text-[11px]">—</div>;
      return (
        <div className="text-gray-600 max-w-[180px] text-[11px] whitespace-pre-wrap break-words">
          {text}
        </div>
      );
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: 'actions',
    cell: ({
      row,
      onEditClick,
    }: {
      row: any;
      onEditClick?: (e: React.MouseEvent, firmId: string) => void;
    }) => {
      const firm = row.original;
      return (
        <div className="text-right">
          <button
            type="button"
            className="text-blue-600 hover:text-blue-900 font-medium"
            onClick={(e) => {
              e.stopPropagation();
              if (onEditClick) {
                onEditClick(e, firm.id);
              }
            }}
          >
            Edit
            <span className="sr-only">, {firm.name}</span>
          </button>
        </div>
      );
    },
  },
];
